import {
  type AssignBody,
  type CommentCreate,
  JobAccepted,
  type OpportunityPatchInput,
  type OverrideCreate,
  type OverrideDisable,
  type UnknownResolve,
} from '@radar/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api, apiUrl, call } from '@/lib/api';
import { qk } from '@/lib/query-keys';

// Accès à `/api/v1/opportunities/:id` (`api.md` § Opportunités). Les routes de lecture et les
// mutations de revue sont typées par `AppType` (T22) ; `refresh-serp`, `reanalyze` et la création
// d'expérience arrivent avec T25 et T26 : `fetch` brut sur le contrat d'`api.md`, réponse validée
// par zod, `404` dégradé proprement par l'appelant.

export const EVENTS_PAGE_SIZE = 20;

export type OpportunityDetailData = Awaited<ReturnType<typeof getOpportunity>>;

export function getOpportunity(id: string) {
  return call(api.opportunities[':id'].$get({ param: { id } }));
}

export const opportunityDetailOptions = (id: string) =>
  queryOptions({ queryKey: qk.opportunities.detail(id), queryFn: () => getOpportunity(id) });

export function listEvents(id: string, page: number) {
  return call(
    api.opportunities[':id'].events.$get({
      param: { id },
      query: { page: String(page), pageSize: String(EVENTS_PAGE_SIZE) },
    }),
  );
}

export function getAnalysis(id: string, analysisId: string) {
  return call(api.opportunities[':id'].analyses[':analysisId'].$get({ param: { id, analysisId } }));
}

export const analysisOptions = (id: string, analysisId: string) =>
  queryOptions({
    queryKey: qk.opportunities.analysis(id, analysisId),
    queryFn: () => getAnalysis(id, analysisId),
  });

export const patchOpportunity = (id: string, json: OpportunityPatchInput) =>
  call(api.opportunities[':id'].$patch({ param: { id }, json }));

export const addComment = (id: string, json: CommentCreate) =>
  call(api.opportunities[':id'].comments.$post({ param: { id }, json }));

export const assignOwner = (id: string, json: AssignBody) =>
  call(api.opportunities[':id'].assign.$post({ param: { id }, json }));

export const createOverride = (id: string, json: OverrideCreate) =>
  call(api.opportunities[':id'].overrides.$post({ param: { id }, json }));

export const disableOverride = (id: string, overrideId: string, json: OverrideDisable) =>
  call(
    api.opportunities[':id'].overrides[':overrideId'].disable.$post({
      param: { id, overrideId },
      json,
    }),
  );

export const resolveUnknown = (id: string, unknownId: string, json: UnknownResolve) =>
  call(
    api.opportunities[':id'].unknowns[':unknownId'].resolve.$post({
      param: { id, unknownId },
      json,
    }),
  );

async function postJson(path: string, body?: unknown): Promise<unknown> {
  return call<unknown>(
    fetch(apiUrl(path), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    }),
  );
}

/** `POST /opportunities/:id/refresh-serp` (route T25) → `202 { jobId }`. */
export async function refreshSerp(id: string, reanalyze: boolean) {
  return JobAccepted.partial({ existing: true }).parse(
    await postJson(`/opportunities/${encodeURIComponent(id)}/refresh-serp`, { reanalyze }),
  );
}

/** `POST /opportunities/:id/reanalyze` (route T25) → `202 { jobId }`. */
export async function reanalyzeOpportunity(id: string) {
  return JobAccepted.partial({ existing: true }).parse(
    await postJson(`/opportunities/${encodeURIComponent(id)}/reanalyze`),
  );
}

export const CreatedExperiment = z.object({ id: z.string() });
export type CreatedExperiment = z.infer<typeof CreatedExperiment>;

/** `POST /opportunities/:id/experiments` (route T26) → `201 { id }`. */
export async function createExperiment(id: string): Promise<CreatedExperiment> {
  return CreatedExperiment.parse(
    await postJson(`/opportunities/${encodeURIComponent(id)}/experiments`),
  );
}

/**
 * Coûts indicatifs affichés avant une action payante (`patterns.md` § 15) tant que le serveur
 * n'expose pas d'estimation : SERP `live` par mot-clé représentatif, analyse profonde (T17).
 */
export const SERP_LIVE_ESTIMATE_USD = 0.002;
export const DEEP_ANALYSIS_ESTIMATE_USD = 0.025;
